
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { AccessTokenData, ConfigService } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'

import type { SeasonEnum } from '../enums'

import type { AppMessages } from '../models/i18n/app.messages'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import InstancesGrid from '../components/instance-selector/instances-grid.component.vue'
import NewInstanceModal from '../components/instance-selector/new-instance-modal.component.vue'

import { UiSnackbarContainer, UiTooltip } from '../components/keen-ui'
import { EpbButton } from '../components/shared/epb-button'
import { mapActions, mapGetters } from '../composables/utils'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { AlignEnum, ColorEnum, DeviceEnum, FeatureIdEnum, RoleEnum } from '../enums'
import { ROUTER_CONSTS } from '../router/router.consts'

import { AUTH_CONSTS } from '../store/auth/auth.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'

import { CONTEXT_CONSTS } from '../store/context/context.consts'

import { INSTANCE_MANAGEMENT_CONSTS } from '../store/instance-management/instance-management.consts'
import { DateUtils, FeaturesTreeUtils } from '../utils'

export default defineComponent({
  name: 'Instances',
  components: {
    InstancesGrid,
    EpbButton,
    UiSnackbarContainer,
    UiTooltip,
    NewInstanceModal,
  },
  emits: {
    contextChanged: () => {},
  },
  setup(_, ctx: SetupContext) {
    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()
    const { emit } = ctx

    const requestNewInstanceModal = ref()

    const messages: AppMessages['instanceSelector'] = $epb.messages.app.instanceSelector

    const snackbarContainer = ref()

    const { authTokenInfo } = mapGetters<AccessTokenData>($store, AUTH_CONSTS.moduleName, {
      authTokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })

    const { instances } = mapGetters<Array<Instance>>($store, CONFIG_CONSTS.moduleName, {
      instances: CONFIG_CONSTS.getters.instances,
    })

    const { updateServiceConfig, setEnabledServiceConfig } = mapActions(
      $store,
      INSTANCE_MANAGEMENT_CONSTS.moduleName,
      {
        updateServiceConfig: INSTANCE_MANAGEMENT_CONSTS.actions.updateServiceConfig,
        setEnabledServiceConfig: INSTANCE_MANAGEMENT_CONSTS.actions.setEnabledServiceConfig,
      },
    )

    const configService = CONTAINER.get<ConfigService>(TYPES.ConfigService)

    const season = computed<SeasonEnum>(() => DateUtils.getSeason(new Date()))

    const seasonImageUrl = computed<string>(() =>
      require(`../assets/media/image/seasons/${season.value}.webp`),
    )

    const columns = computed<number>(() => {
      const isSmallDevice = $epb.device <= DeviceEnum.SMALL_TABLET
      return isSmallDevice ? 2 : 4
    })

    const userIsPartner = computed<boolean>(() => {
      return (
        authTokenInfo.value?.authorization?.roles['*']?.includes(RoleEnum.PARTNER_REQUESTS) || false
      )
    })

    const noInstancesAvailableText = computed<string>(() => {
      const noInstancesFound = messages.noInstancesFound
      return userIsPartner.value ? noInstancesFound.partner : noInstancesFound.default
    })

    const createInstanceVisible = computed<boolean>(() => {
      return !!authTokenInfo.value?.authorization.resources.find(
        resource => resource.name.toLowerCase() === FeatureIdEnum.CREATE_INSTANCE.toLowerCase(),
      )
    })

    const indexVisible = computed<boolean>(() => {
      return instances.value.some(instance =>
        FeaturesTreeUtils.hasFeatureNodePermission(
          FeatureIdEnum.INSTANCE_MANAGEMENT,
          FeaturesTreeUtils.getFeaturesTree(instance)[DomainEnum.GLOBAL],
        ),
      ) // TODO Manage Index permissions properly
    })

    const userRoleManagementVisible = computed<boolean>(() => {
      return instances.value.some(instance => {
        return FeaturesTreeUtils.hasFeatureNodePermission(
          FeatureIdEnum.USER_ROLE_MANAGEMENT,
          FeaturesTreeUtils.getFeaturesTree(instance)[DomainEnum.GLOBAL],
        )
      })
    })

    const { setContext } = mapActions($store, CONTEXT_CONSTS.moduleName, {
      setContext: CONTEXT_CONSTS.actions.setContext,
    })

    const onInstanceClick = (instance: Instance): void => {
      setContext({ instanceId: instance.id })
      emit('contextChanged')
    }

    const showSnackbar = (message: string): void => {
      snackbarContainer.value?.createSnackbar({ message })
    }

    const openRequestNewInstanceModal = (): void => {
      requestNewInstanceModal.value?.open()
    }

    const saveInstance = async (instance: string): Promise<void> => {
      try {
        await configService.createInstance(instance)
        await updateServiceConfig({
          dto: {
            instance,
            service: 'playboard',
            data: {},
          },
        })
        await setEnabledServiceConfig({
          config: {
            service: 'playboard',
            instance,
          },
          enabled: true,
        })
        showSnackbar($epb.messages.instanceManagement.requestNewInstance.success)
      } catch (error) {
        await configService.deleteInstance(instance)
        showSnackbar($epb.messages.instanceManagement.requestNewInstance.error)
      } finally {
        requestNewInstanceModal.value?.close()
      }
    }

    return {
      messages,
      authTokenInfo,
      requestNewInstanceModal,
      snackbarContainer,
      instances,
      season,
      seasonImageUrl,
      columns,
      noInstancesAvailableText,
      userIsPartner,
      createInstanceVisible,
      indexVisible,
      userRoleManagementVisible,
      onInstanceClick,
      showSnackbar,
      openRequestNewInstanceModal,
      saveInstance,
      ROUTER_CONSTS,
      FeatureIdEnum,
      AlignEnum,
      ColorEnum,
      DeviceEnum,
      RoleEnum,
    }
  },
})
